import "./ForgotPasswordModal.css";
import { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import SubmitButton from "../../SubmitButton/SubmitButton";
import InputField from "../../CredentialsInput/InputField";
import xIcon from "../../../icons/X.svg"
import ReCAPTCHA from "react-google-recaptcha";
import { clientConfig } from "../../../config";

import * as translator from "../../../utils/translator";
import * as http from "../../../utils/http";
import useForm from '../../../customHooks/useForm';

// With `email` (a logged-in customer) the email form is skipped: the link is sent on mount and only the "sent" view shows.
export default function ForgotPasswordModal({ closeModal, language, setStickyNotificationDetails, onSubmitted, email }:
    { closeModal: any, language: string, setStickyNotificationDetails: any, onSubmitted?: () => void, email?: string }) {
    const { country } = useParams();
    const [showSubmitted, setShowSubmitted] = useState(false);
    const forgotPasswordFormAttributes = useForm({ email: "" }, language, undefined, { looseEmail: true });
    const reRef = useRef<ReCAPTCHA>(null);

    const sendResetLink = async (emailToSend: string) => {
        try {
            let captchaToken;
            if (clientConfig.IS_RECAPTCHA_ENABLED) {
                captchaToken = await reRef.current?.executeAsync();
                reRef.current?.reset();
            }

            const response = await http.postToServer(`reset-password`, { email: emailToSend, country, language, emailType:'resetPassword', captchaToken });
            if (response.data?.status === "blocked") {
                closeModal();
                setStickyNotificationDetails((currentDetails: any) => ({
                    show: true,
                    title: translator.translate(language, "default", "StickyNotification/danger_title"),
                    content: translator.translate(language, "default", "StickyNotification/reset_password_user_blocked"),
                    level: "error",
                    showInfoIcon: true,
                    refresh: !currentDetails.refresh
                }));
                return;
            }
            setShowSubmitted(true);
            onSubmitted?.();
        } catch (error) {
            window.clientLogger.error("error forgot password: ", error);
            // Without the form nothing is on screen, so close and tell the customer; they can press the button again.
            if (email) {
                closeModal();
                setStickyNotificationDetails((currentDetails: any) => ({
                    show: true,
                    title: translator.translate(language, "default", "StickyNotification/generic_server_error_title"),
                    content: "",
                    level: "error",
                    showInfoIcon: false,
                    refresh: !currentDetails.refresh
                }));
            }
        }
    };

    useEffect(() => {
        if (email) sendResetLink(email);
    }, []);

    const onSubmit = async (e : React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (forgotPasswordFormAttributes.validateForm()) {
            sendResetLink(forgotPasswordFormAttributes.form.email);
        }
    };

    const recaptcha = clientConfig.IS_RECAPTCHA_ENABLED && (
        <ReCAPTCHA
            sitekey={clientConfig.RECAPTCHA_SITE_KEY}
            size="invisible"
            ref={reRef}
        />
    );

    if (email && !showSubmitted) return <>{recaptcha}</>;


    return (
        <>
            <div
                className="forgotPasswordModal-background bg-[#00000080]"
                onClick={(event) => {
                    closeModal();
                }}
            >
                {/*event.stopPropagation => so it won't close the modal when clicking on form fields */}
                <div id="forgot-password-modal" className="relative p-[24px] max-w-[460px] z-[1001] flex flex-col items-center justify-center bg-white mx-[15px] rounded-[16px]" onClick={(event) => event.stopPropagation()}>
                    <img onClick={() => closeModal()} id="forgot-password-modal-x" className="absolute top-[24px] right-[24px] cursor-pointer p-[2px] w-[22px] h-[22px]" style={{ filter: 'brightness(0)' }} src={xIcon} alt="x" />

                    {/* If submitted - show sent window */}
                    {showSubmitted ? (
                        <>
                            <p 
                                id="forgotPasswordModal-title-sent"
                                className="font-poppins text-[16px] md:text-[22px] font-[600] text-[#61A333] text-left pr-[2em] mb-[12px] mr-auto"
                            >
                                {translator.translate(
                                    language,
                                    "default",
                                    "ForgotPasswordModal/sent_reset_title"
                                )}
                            </p>
                            <p className="text-[#616975] text-left text-[400] text-[14px] leading-[1.5] font-roboto mb-[24px]">
                                {translator.translate(
                                    language,
                                    "default",
                                    "ForgotPasswordModal/sent_reset_text"
                                )}
                            </p>

                            <button
                                id="forgotPasswordModal-close-button"
                                className="font-poppins text-[#61A333] text-[14px] md:text-[20px] font-[500] text-center w-full rounded-[12px] bg-[#ffffff] border !border-[#61A333] py-[12px] px-[24px] cursor-pointer outline-none shadow-none appearance-none focus:!border-[#61A333] active:!border-[#61A333] focus:ring-0"
                                onClick={() => closeModal()}
                            >
                                {translator.translate(
                                    language,
                                    "default",
                                    "ForgotPasswordModal/close_button"
                                )}
                            </button>
                        </>
                    ) : (  /*   If not submitted - show reset password window    */
                        <form onSubmit={onSubmit}>
                            <p className="font-poppins text-[22px] font-[600] text-black leading-[1] text-left pr-[2em] mb-[24px]">
                                {translator.translate(
                                    language,
                                    "default",
                                    "ForgotPasswordModal/password_reset_title"
                                )}
                            </p>
                            <p className="text-[#616975] text-left text-[400] text-[14px] leading-[1.5] font-roboto mb-[24px]">
                                {translator.translate(
                                    language,
                                    "default",
                                    "ForgotPasswordModal/password_reset_text"
                                )}
                            </p>

                            <InputField
                                value={forgotPasswordFormAttributes.form.email}
                                onChange={(value: string) => {
                                    forgotPasswordFormAttributes.updateForm("email", value)
                                }}
                                type="email"
                                placeholder={"E-Mail"}
                                language={language}
                                mandatory
                                errorMsg={forgotPasswordFormAttributes.errorMessagePerField.email}
                                onBlur={() => forgotPasswordFormAttributes.onFieldBlur("email")}
                                touched={forgotPasswordFormAttributes.touched.email}
                                inputClassName="!rounded-[12px] !py-0"
                                labelClassName="!static"
                                placeholderClassName="!top-0 !bottom-auto !leading-[56px]"
                                placeholderFocusedClassName="!hidden"
                            />

                            {recaptcha}

                            <SubmitButton
                                id={"submit-button"}
                                isEnabledState={true}
                                fontWeight={"500"}
                                language={language}
                                onSubmit={onSubmit}
                                width={"100%"}
                                fontsize={"20px"}
                                className="linear-background-color-primary rounded-[12px]"
                                placeholder={translator.translate(
                                    language,
                                    "default",
                                    "ForgotPasswordModal/submit"
                                )}
                            />
                        </form>
                    )}
                </div>
            </div>
        </>
    );
}
